import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { moneyDisplay } from "@icraftx/shared/v1";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DocSlip, printDocSlip } from "@/modules/docs/print/DocSlip";
import { buildJournalPrint } from "@/modules/docs/print/doc-print-math";
import { DataTable } from "@/modules/shell/data-table";
import { DocActivity } from "@/modules/shell/DocActivity";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import { TagsAssigneesPanel } from "@/modules/tags/components/TagsAssigneesPanel";
import { fetchJournal } from "@/shared/api/accounting";
import { docLink } from "@/shared/doc-link";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";
import { journalLineColumns } from "./journal-detail-columns";

export function JournalEntryDetailPage() {
  const { companySlug, journalId } = useParams();

  const journalQuery = useQuery({
    queryKey: queryKeys.journals.detail(companySlug ?? "", journalId ?? ""),
    enabled: Boolean(companySlug && journalId),
    queryFn: () => fetchJournal(companySlug!, journalId!),
  });
  const errorMsg = queryErrorMessage(journalQuery.error, journalQuery.isError);
  useAuthRedirectOnQueryError(errorMsg);

  if (!companySlug || !journalId) return null;

  const displayError =
    errorMsg && errorMsg !== "Not signed in" ? errorMsg : null;

  if (journalQuery.isPending || !journalQuery.data) {
    return (
      <PageFrame width="narrow" className="py-4">
        {displayError ? (
          <Alert variant="destructive">{displayError}</Alert>
        ) : (
          <Alert variant="muted">Loading journal…</Alert>
        )}
      </PageFrame>
    );
  }

  const data = journalQuery.data;
  const { journal: je, lines } = data;
  const sourceHref = docLink(
    companySlug,
    je.sourceDocType,
    je.sourceDocId,
  );
  const printData = buildJournalPrint({
    number: je.number,
    lifecycle: je.lifecycle,
    kind: je.kind,
    memo: je.memo,
    sourceDocType: je.sourceDocType,
    totalDebit: data.totalDebit,
    totalCredit: data.totalCredit,
    issuedAt: je.entryDate,
    lines: lines.map((l) => ({
      accountCode: l.accountCode,
      accountName: l.accountName,
      debit: l.debit,
      credit: l.credit,
      memo: l.memo,
    })),
  });

  return (
    <PageFrame width="narrow">
      <div className="pos-receipt-print" aria-hidden>
        <DocSlip data={printData} />
      </div>
      <div className="no-print">
        <PageHeader
          eyebrow="Journal"
          title={je.number}
          description={[je.lifecycle, je.kind, je.memo]
            .filter(Boolean)
            .join(" · ")}
          actions={
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => printDocSlip()}
            >
              Print
            </Button>
          }
        />
        <TagsAssigneesPanel
          companySlug={companySlug}
          docType="journal_entry"
          docId={journalId}
          linkPath={`/c/${companySlug}/gl/journals/${journalId}`}
        />
      </div>

      <dl className="mt-6 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-[var(--color-muted)]">Date</dt>
        <dd className="m-0">{je.entryDate}</dd>
        {je.postedAt && (
          <>
            <dt className="text-[var(--color-muted)]">Posted</dt>
            <dd className="m-0 text-xs text-[var(--color-muted)]">
              {new Date(je.postedAt).toLocaleString()}
            </dd>
          </>
        )}
        {je.sourceDocType && (
          <>
            <dt className="text-[var(--color-muted)]">Source</dt>
            <dd className="m-0">
              {sourceHref ? (
                <Link to={sourceHref} className="no-underline">
                  {je.sourceDocType}
                </Link>
              ) : (
                je.sourceDocType
              )}
            </dd>
          </>
        )}
        {je.notes && (
          <>
            <dt className="text-[var(--color-muted)]">Notes</dt>
            <dd className="m-0">{je.notes}</dd>
          </>
        )}
      </dl>

      <DataTable
        listId="journal-detail-lines"
        companySlug={companySlug}
        className="mt-8"
        columns={journalLineColumns(companySlug)}
        data={lines}
        getRowId={(r) => r.id}
        manualSorting={false}
        empty={
          <EmptyState
            title="No lines"
            description="This journal has no account lines."
          />
        }
      />
      <p className="mt-3 m-0 flex flex-wrap justify-between gap-3 text-sm font-semibold">
        <span>Total · {data.currencyCode}</span>
        <span className="font-mono tabular-nums">
          Dr {moneyDisplay(data.totalDebit)} · Cr{" "}
          {moneyDisplay(data.totalCredit)}
        </span>
      </p>

      {displayError && (
        <Alert variant="destructive" className="mt-4">
          {displayError}
        </Alert>
      )}

      <DocActivity
        companySlug={companySlug}
        docType="journal_entry"
        docId={journalId}
        linkPath={`/c/${companySlug}/gl/journals/${journalId}`}
      />
    </PageFrame>
  );
}
